import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n';

interface InputDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  confirmText: string;
  cancelText: string;
  isLoading?: boolean;
  defaultValue?: string;
  placeholder?: string;
  onConfirm: (value: string) => void;
  onClose: () => void;
  className?: string;
}

export default function InputDialog({
  isOpen,
  title,
  description,
  confirmText,
  cancelText,
  isLoading = false,
  defaultValue = '',
  placeholder,
  onConfirm,
  onClose,
  className,
}: InputDialogProps) {
  const [value, setValue] = useState(defaultValue);
  const tCommon = useTranslations('common');
  const tValidation = useTranslations('validation');

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [defaultValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = () => {
    if (!value.trim()) {
      return;
    }
    onConfirm(value.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div
        className={cn(
          'flex w-full max-w-[30rem] flex-col gap-5 rounded-3xl border border-[#f1f1f1] bg-white p-6 shadow-[0px_12px_32px_-16px_rgba(31,31,31,0.2)]',
          className
        )}
      >
        <div className="flex flex-col gap-2">
          <h2 className="text-[18px] leading-7 font-bold text-[#292929]">{title}</h2>
          {description && <p className="text-[15px] leading-6 text-[#666666]">{description}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="dialog-comment" className="text-sm font-medium text-[#292929]">
            {placeholder ?? ''}
          </label>
          <textarea
            id="dialog-comment"
            rows={4}
            className="w-full resize-none rounded-2xl border border-[#e4e4e4] bg-[#fbfbfb] px-4 py-3 text-[15px] leading-6 text-[#292929] transition-colors outline-none focus:border-[#242424]"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            disabled={isLoading}
          />
          {!value.trim() && (
            <p className="text-sm text-[#e5484d]">{tValidation('commentRequired')}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-full px-6 py-2 text-[15px] leading-6 text-[#666666] hover:bg-[#f5f5f5]"
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant="brand"
            onClick={handleConfirm}
            disabled={isLoading || !value.trim()}
            className="flex items-center gap-2 rounded-full px-6 py-2 text-[15px] leading-6 text-white"
          >
            {isLoading ? tCommon('loading') : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
